import { ApiClient, ApiError } from "@/api/ApiClient";
import outputs from "../../amplify_outputs.json";

/**
 * URL base del API externa, publicado por el backend en `custom.PublicApiUrl`
 * (ver amplify/infra/public-api.ts). Mismo patron que `EMAIL_ASSETS_CDN_URL`.
 */
export const PUBLIC_API_URL = (
    outputs as unknown as { custom?: { PublicApiUrl?: string } }
).custom?.PublicApiUrl ?? "";

/** URL de consumo de un recurso para un tenant. Es lo que el usuario copia y comparte. */
export function buildPublicApiUrl(tenantId: string, resourceName: string): string {
    if (!PUBLIC_API_URL || !tenantId) return "";
    return `${PUBLIC_API_URL}/v1/${tenantId}/${resourceName}.json`;
}

export type RegeneratePublicApiResponse = {
    itemsCount: number;
    generatedAt?: string | null;
};

/** Error del throttle del boton "Regenerar ahora". Trae el tiempo de espera. */
export class PublicApiThrottledError extends Error {
    retryAfterSeconds: number;
    constructor(retryAfterSeconds: number) {
        super("La regeneracion ya se solicito hace poco. Intenta de nuevo mas tarde.");
        this.name = "PublicApiThrottledError";
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

export async function regeneratePublicApi(): Promise<RegeneratePublicApiResponse> {
    const { data, errors } = await ApiClient.mutations.regeneratePublicApiMutation();
    if (errors?.length) {
        throw new ApiError("No fue posible regenerar el API", "API_ERROR", errors);
    }
    if (!data?.ok) {
        if (data?.error === "throttled") {
            throw new PublicApiThrottledError(data.retryAfterSeconds || 900);
        }
        throw new Error(data?.error || "No fue posible regenerar el API");
    }
    return { itemsCount: data.itemsCount || 0, generatedAt: data.generatedAt };
}

export type RotatePublicApiPasswordResponse = {
    username: string;
    password: string;
    updatedAt?: string | null;
};

export async function rotatePublicApiPassword(): Promise<RotatePublicApiPasswordResponse> {
    const { data, errors } = await ApiClient.mutations.rotatePublicApiPasswordMutation();
    if (errors?.length) {
        throw new ApiError("No fue posible generar las credenciales", "API_ERROR", errors);
    }
    if (!data?.ok) {
        throw new Error(data?.error || "No fue posible generar las credenciales");
    }
    return {
        username: data.username || "",
        password: data.password || "",
        updatedAt: data.updatedAt,
    };
}
